/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { InsightsModel } from '../models';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subject } from 'rxjs';
import { SecurityApiEnum } from '../enums/api';

@Injectable()
export class SecurityApiService {
    /**
     * Emit when get insights is called.
     */
    public emitSecurityResponse: Subject<any> = new Subject<any>();

    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get retrieves all security for a namespace.
     * @param namespaceId
     * @param options
     */
    public getAllSecurity(namespaceId: number, options?: any): Observable<InsightsModel[]> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(SecurityApiEnum.GET_SECURITY_ALL(namespaceId), moduleAuth)
                .subscribe(
                    (data: InsightsModel[]): void => {
                        this.emitSecurityResponse.next(data);
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Get retrieves all security for a namespace by ID.
     * @param namespaceId
     * @param adcId
     * @param options
     */
    public getSecurityInsightByAdc(namespaceId: number, adcId: string, options?: any): Observable<InsightsModel> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(SecurityApiEnum.GET_SECURITY_INSIGHTS_BY_ADC(namespaceId, adcId), moduleAuth)
                .subscribe(
                    (data: InsightsModel): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Get retrieves all security for a namespace.
     * @param namespaceId
     * @param options
     */
    public getAllCertificates(namespaceId: number, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(
                SecurityApiEnum.GET_CERTIFICATES_ALL(namespaceId),
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get retrieves all security for a namespace by ID.
     * @param namespaceId
     * @param adcId
     * @param options
     */
    public getCertificatesByAdc(namespaceId: number, adcId: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(
                SecurityApiEnum.GET_CERTIFICATES_BY_ADC(namespaceId, adcId),
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
